import { useEffect, useRef } from "react";
import { useOakPad } from "@/context/OakPadContext";
import { toast } from "sonner";

const LONG_PRESS_MS = 550;

/**
 * Wraps any block of course text and turns a long-press (or right-click on
 * desktop) into a "Clip to OAK-Pad" capture. We capture the active text
 * selection if one exists; otherwise we fall back to the wrapped child's
 * full text content. Works on touch + mouse without preventing normal
 * text selection on quick taps.
 */
export default function LongPressClipper({
  children,
  pathId,
  stepIndex,
  title,
  className,
  asTag: Tag = "div",
  ...rest
}) {
  const { addSnippet } = useOakPad();
  const ref = useRef(null);
  const timer = useRef(null);
  const longPressed = useRef(false);

  const capture = () => {
    let text = "";
    try {
      const sel = window.getSelection?.();
      const selText = sel ? String(sel).trim() : "";
      if (selText) {
        text = selText;
      } else if (ref.current) {
        text = ref.current.innerText || ref.current.textContent || "";
        text = text.trim().slice(0, 4000);
      }
    } catch {
      // ignore
    }
    if (!text) return;
    const clip = addSnippet(text, { path_id: pathId, step_index: stepIndex, title });
    if (clip) toast.success("Clipped to OAK-Pad", { description: text.slice(0, 80) + (text.length > 80 ? "…" : "") });
  };

  const startTimer = () => {
    longPressed.current = false;
    clearTimer();
    timer.current = setTimeout(() => {
      longPressed.current = true;
      capture();
    }, LONG_PRESS_MS);
  };

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => () => clearTimer(), []);

  const handleContextMenu = (e) => {
    // Desktop right-click → instant clip; suppress the native menu only
    // when we successfully capture (so right-click on links etc is intact).
    const sel = window.getSelection?.();
    const hasSel = sel && String(sel).trim().length > 0;
    if (hasSel) {
      e.preventDefault();
      capture();
    }
  };

  return (
    <Tag
      ref={ref}
      onTouchStart={startTimer}
      onTouchEnd={clearTimer}
      onTouchCancel={clearTimer}
      onTouchMove={clearTimer}
      onMouseDown={startTimer}
      onMouseUp={clearTimer}
      onMouseLeave={clearTimer}
      onContextMenu={handleContextMenu}
      className={className}
      data-testid="long-press-clipper"
      {...rest}
    >
      {children}
    </Tag>
  );
}
